import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Box, Breadcrumbs, FormControlLabel, Checkbox, FormHelperText } from "@mui/material";
import Grid from "@mui/material/Grid";
import GridTable from "../../../components/GridTable";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import Dropdown from "../../../components/Dropdown";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import TextField from "@mui/material/TextField";
import ActionMenu from "../../../components/ActionMenu";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
  getCompanyConfiguration,
  createCompanyConfiguration,
  updateCompanyConfiguration,
  deleteCompanyConfiguration,
} from "./api";
import { getAssetTypes } from "../api";
import { getErrorMessage } from "../../../utils/error";

const Configuration = () => {
  const location = useLocation();
  const { company_id } = useParams();
  const company_name =
    location.state?.company_name ||
    localStorage.getItem("company_name") ||
    "Company";

  const [configs, setConfigs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [loading, setLoading] = useState({
    fetch: false,
    create: false,
    update: false,
    delete: false,
    assetTypes: false,
  });
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const validationSchema = Yup.object().shape({
    assetTypes: Yup.string().required("Asset type is required"),
  });

  const columns = [
    {
      field: "asset_types",
      headerName: "Asset Types",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => params.value?.join(", ") || "—",
    },
    {
      field: "is_auto_mail_enabled",
      headerName: "Auto Mail Enabled",
      headerClassName: "table-header",
      flex: 1,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <EditIcon color="primary" />,
              tooltip: "Edit",
              onClick: handleEdit,
            },
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
          ]}
        />
      ),
      flex: 1,
    },
  ];

  const fetchAssetTypes = async () => {
    setLoading((p) => ({ ...p, assetTypes: true }));
    try {
      const res = await getAssetTypes();
      if (res.status && res.data) {
        setAssetTypeOptions(res.data);
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
    } finally {
      setLoading((p) => ({ ...p, assetTypes: false }));
    }
  };

  useEffect(() => {
    const fetchConfiguration = async () => {
      setLoading((p) => ({ ...p, fetch: true }));
      try {
        const res = await getCompanyConfiguration(company_id);
        if (res.status && res.data) {
          setConfigs([{ ...res.data, id: res.data.company_id }]);
        } else {
          setConfigs([]);
        }
      } catch (err) {
        // Not found -> no configuration yet
        setConfigs([]);
      } finally {
        setLoading((p) => ({ ...p, fetch: false }));
      }
    };

    if (company_id) fetchConfiguration();
  }, [company_id]);

  const openDialog = () => {
    fetchAssetTypes();
    setDialogOpen(true);
  };

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
    setEditConfig(null);
  };

  const handleEdit = (params) => {
    setEditConfig(params.row);
    openDialog();
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const confirmDialog = async (values, { resetForm }) => {
    const success = editConfig
      ? await handleUpdate(values)
      : await handleCreate(values);

    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((p) => ({ ...p, create: true }));
    try {
      const payload = {
        company_id: company_id,
        is_auto_mail_enabled: values.isAutoMailEnabled,
        asset_types: values.assetTypes ? [values.assetTypes] : [],
      };

      const res = await createCompanyConfiguration(payload);
      if (res.status) {
        setConfigs([{ ...payload, id: company_id }]);
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, create: false }));
    }
  };

  const handleUpdate = async (values) => {
    setLoading((p) => ({ ...p, update: true }));
    try {
      const payload = {
        company_id: company_id,
        is_auto_mail_enabled: values.isAutoMailEnabled,
        asset_types: values.assetTypes ? [values.assetTypes] : [],
      };

      const res = await updateCompanyConfiguration(payload);
      if (res.status) {
        setConfigs([{ ...payload, id: company_id }]);
        successMessage(res.message);
        return true;
      } else {
        errorMessage(res.message);
        return false;
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
      return false;
    } finally {
      setLoading((p) => ({ ...p, update: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoading((p) => ({ ...p, delete: true }));
    try {
      const res = await deleteCompanyConfiguration(company_id);
      if (res.status) {
        setConfigs([]);
        successMessage(res.message);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(getErrorMessage(err));
    } finally {
      setLoading((p) => ({ ...p, delete: false }));
      setDeleteOpen(false);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box>
          <main className="m-a-50">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px",
              }}
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                <Link color="primary" to="/company">
                  Company
                </Link>
                <Link color="primary" to={`/company`}>
                  {company_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/configuration`}
                  state={{ company_name: company_name }}
                >
                  Configuration
                </Link>
              </Breadcrumbs>
            </div>
            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {configs.length === 0 && (
                  <Button
                    label="Add"
                    variant="contained"
                    color="primary"
                    onClick={openDialog}
                  />
                )}
              </div>
            </div>
            <div
              style={{ height: "100%", width: "100%" }}
              className="ag-theme-alpine m-t-30"
            >
              <GridTable
                rows={configs}
                columns={columns}
                pageSize={10}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.id}
                density="standard"
              />
            </div>
          </main>
        </Box>
      )}
      <Formik
        initialValues={{
          isAutoMailEnabled: editConfig ? editConfig.is_auto_mail_enabled : false,
          assetTypes: editConfig ? (editConfig.asset_types?.[0] ?? "") : "",
        }}
        validationSchema={validationSchema}
        onSubmit={confirmDialog}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => (
          <>
            <Dialog
              open={isDialogOpen}
              onClose={() => closeDialog(resetForm)}
              onConfirm={handleSubmit}
              title={
                !loading.update && !loading.create
                  ? editConfig
                    ? "Edit Configuration"
                    : "Add Configuration"
                  : ""
              }
              confirmButtonText={
                !loading.update && !loading.create
                  ? editConfig
                    ? "Update"
                    : "Add"
                  : ""
              }
              cancelButtonText={
                !loading.update && !loading.create ? "Cancel" : ""
              }
              icon={<SettingsIcon />}
              message={
                <Box padding={4}>
                  {loading.update || loading.create ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <Dropdown
                            options={assetTypeOptions}
                            value={values.assetTypes ?? ""}
                            onChange={(value) =>
                              setFieldValue("assetTypes", value)
                            }
                            label="Asset Type"
                            width="200px"
                            required
                            error={Boolean(
                              touched.assetTypes && errors.assetTypes,
                            )}
                            helperText={
                              touched.assetTypes && errors.assetTypes
                                ? String(errors.assetTypes)
                                : undefined
                            }
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="isAutoMailEnabled"
                                name="isAutoMailEnabled"
                                checked={values.isAutoMailEnabled}
                                onChange={handleChange}
                                color="primary"
                              />
                            }
                            label="Enable Auto Mail?"
                          />
                          {touched.isAutoMailEnabled &&
                            errors.isAutoMailEnabled && (
                              <FormHelperText error>
                                {errors.isAutoMailEnabled}
                              </FormHelperText>
                            )}
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Box>
              }
            />
          </>
        )}
      </Formik>
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={!loading.delete ? "Delete Configuration" : ""}
        confirmButtonText={!loading.delete ? "Confirm" : ""}
        cancelButtonText={!loading.delete ? "Cancel" : ""}
        icon={<DeleteIcon />}
        message={
          <Box padding={2}>
            {loading.delete ? (
              <Loader color="primary" size={40} />
            ) : (
              "Are you sure you want to delete this configuration?"
            )}
          </Box>
        }
      />
    </>
  );
};

export default Configuration;